import { useState } from 'react';
import { Alert, Button, Card, Icon, Metric, Table, cn, type Column } from '@evogard/ui';
import type { ManagementRepairMoney } from '@evogard/contract';
import {
  EXPENSE_STAGE_LABELS,
  cents,
  count,
  sum,
  format,
  fromColumn,
  type ClaimFlowPeriod,
  type Money,
} from '@evogard/domain';
import { useManagementRepairMoney } from './useManagement.js';

/**
 * O dinheiro do Reparo (29/10/2026, pedido do dono): o Reparo é o departamento
 * que mais gera despesa. Dois cartões no fim do Painel da Gestão do sinistro —
 * as despesas que o Reparo pediu ao Financeiro, e a orçamentação.
 *
 * **O período aqui é o do LANÇAMENTO** (decisão do dono): a despesa e o
 * orçamento lançados nos últimos N dias, e não os dos sinistros abertos neles,
 * como nos outros gráficos — e cada cartão diz isso. A conta é do domínio
 * (`summarizeRepairMoney`). Os valores chegam em texto decimal e viram `Money`
 * aqui; proporção de barra é a única conta em `number`.
 */

type Data = ManagementRepairMoney;

const money = (text: string): Money => fromColumn(text)!;
const brl = (text: string) => format(money(text));
const ratio = (part: string, whole: string) => {
  const w = money(whole);
  return w > 0 ? money(part) / w : 0;
};
const pctOf = (part: string, whole: string) => `${Math.round(ratio(part, whole) * 100)}%`;

export function RepairMoneyCharts({ days }: { days: ClaimFlowPeriod }) {
  const query = useManagementRepairMoney(days);
  const data = query.data;
  return (
    <>
      <RepairExpensesCard days={days} data={data} error={query.error} />
      <RepairQuotesCard days={days} data={data} error={query.error} />
    </>
  );
}

function Failed({ title, error }: { title: string; error: Error }) {
  return (
    <Alert tone="erro" title={title}>
      Nada foi desenhado, para não mostrar número velho como se fosse atual.
      <span className="mt-1 block font-mono text-xs">{error.message}</span>
    </Alert>
  );
}

function Skeleton() {
  return (
    <div className="h-48 animate-pulse rounded-borda bg-superficie-suave" aria-busy="true" />
  );
}

/* ------------------------------------------------------------------------ */

/**
 * As despesas do Reparo: pedido, aprovado e pago lado a lado, cada um dentro do
 * anterior — a barra do aprovado é a fração do pedido, a do pago a do pedido
 * também, para as três se lerem na mesma régua. O recusado fica à parte.
 */
function RepairExpensesCard({
  days,
  data,
  error,
}: {
  days: ClaimFlowPeriod;
  data: Data | undefined;
  error: Error | null;
}) {
  const [asTable, setAsTable] = useState(false);
  const e = data?.expenses;
  const any = !!e && (e.requested.count > 0 || e.refused.count > 0);
  const rows = e
    ? [
        { key: 'pedido', label: 'Pedido', value: e.requested, tone: 'opacity-35' },
        { key: 'aprovado', label: 'Aprovado pelo Financeiro', value: e.approved, tone: 'opacity-65' },
        { key: 'pago', label: 'Pago', value: e.paid, tone: '' },
      ]
    : [];
  return (
    <Card
      as="figure"
      title="Despesas do Reparo"
      descricao={`O que o Reparo pediu ao Financeiro, lançado nos últimos ${days} dias: pedido, aprovado e pago.`}
      actions={
        any &&
        !error && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver por fase'}
          </Button>
        )
      }
    >
      {error ? (
        <Failed title="Não conseguimos carregar as despesas do Reparo" error={error} />
      ) : !e ? (
        <Skeleton />
      ) : !any ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          O Reparo não lançou despesa nos últimos {days} dias.
        </p>
      ) : asTable ? (
        <StagesTable data={data!} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Metric
              label="Pedido"
              value={brl(e.requested.total)}
              support={count(e.requested.count, 'despesa', 'despesas')}
            />
            <Metric
              label="Aprovado"
              value={brl(e.approved.total)}
              support={`${pctOf(e.approved.total, e.requested.total)} do pedido`}
            />
            <Metric
              label="Pago"
              value={brl(e.paid.total)}
              support={count(e.paid.count, 'despesa paga', 'despesas pagas')}
            />
            <Metric
              label="Recusado"
              value={brl(e.refused.total)}
              tone={e.refused.count > 0 ? 'erro' : undefined}
              support={count(e.refused.count, 'despesa', 'despesas')}
            />
          </div>
          <ol className="flex flex-col gap-2" aria-label="Pedido, aprovado e pago">
            {rows.map((r) => (
              <li
                key={r.key}
                className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)_minmax(0,7rem)] items-center gap-3 text-xs"
              >
                <span className="text-texto-suave">{r.label}</span>
                <span className="h-2.5 overflow-hidden rounded-pilula bg-superficie-suave">
                  <span
                    className={cn('block h-full rounded-pilula bg-primaria', r.tone)}
                    style={{
                      width: `${ratio(r.value.total, e.requested.total) * 100}%`,
                    }}
                  />
                </span>
                <span className="text-right font-semibold tabular-nums text-texto">
                  {brl(r.value.total)}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-texto-suave">
            Pago é o valor que saiu, quando o Financeiro o registrou diferente do pedido.
            O recusado — reprovado pelo supervisor ou recusado pelo Financeiro — não entra
            no pedido.
          </p>
        </div>
      )}
    </Card>
  );
}

type StageRow = Data['expenses']['byStage'][number];

function StagesTable({ data }: { data: Data }) {
  const columns: Column<StageRow>[] = [
    { title: 'Fase no Financeiro', cell: (r) => EXPENSE_STAGE_LABELS[r.stage], alignment: 'inicio' },
    { title: 'Despesas', cell: (r) => r.count, alignment: 'fim' },
    { title: 'Valor pedido', cell: (r) => brl(r.total), alignment: 'fim' },
  ];
  return (
    <Table
      columns={columns}
      items={data.expenses.byStage}
      rowKey={(r) => r.stage}
      label={`Despesas do Reparo por fase, lançadas nos últimos ${data.days} dias`}
      empty="Nenhuma despesa do Reparo no período."
    />
  );
}

/* ------------------------------------------------------------------------ */

/**
 * A orçamentação do Reparo: o bruto e o aprovado do orçamento mais recente de
 * cada sinistro, e duas divisões — peças × mão de obra, e o que a associação
 * paga × o que o associado paga.
 */
function RepairQuotesCard({
  days,
  data,
  error,
}: {
  days: ClaimFlowPeriod;
  data: Data | undefined;
  error: Error | null;
}) {
  const q = data?.quotes;
  return (
    <Card
      as="figure"
      title="Orçamentação do Reparo"
      descricao={`Os orçamentos de oficina e do CILIA lançados nos últimos ${days} dias — o mais recente de cada sinistro.`}
    >
      {error ? (
        <Failed title="Não conseguimos carregar a orçamentação do Reparo" error={error} />
      ) : !q ? (
        <Skeleton />
      ) : q.claims === 0 ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum orçamento do Reparo lançado nos últimos {days} dias.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Metric
              label="Orçado (bruto)"
              value={brl(q.gross)}
              support={count(q.claims, 'sinistro', 'sinistros')}
            />
            <Metric
              label="Aprovado"
              value={brl(q.approved)}
              support={`${pctOf(q.approved, q.gross)} do orçado`}
            />
            <Metric
              label="Média por sinistro"
              value={
                q.claims - q.withoutAmount > 0
                  ? format(cents(Math.round(money(q.gross) / (q.claims - q.withoutAmount))))
                  : '—'
              }
              support="do orçado, entre os que têm valor"
            />
          </div>
          <Split
            label="Peças e mão de obra"
            left={{ label: 'Peças', value: q.parts }}
            right={{ label: 'Mão de obra', value: q.labor }}
          />
          <Split
            label="Quem paga"
            left={{ label: 'Associação', value: q.associationShare }}
            right={{ label: 'Associado', value: q.memberShare }}
          />
          {q.withoutAmount > 0 && (
            <p className="text-xs text-texto-suave">
              {count(q.withoutAmount, 'orçamento ainda está', 'orçamentos ainda estão')} sem
              valor bruto, e não entra{q.withoutAmount === 1 ? '' : 'm'} nas somas.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/** Uma barra dividida em duas partes, com os dois valores escritos. */
function Split({
  label,
  left,
  right,
}: {
  label: string;
  left: { label: string; value: string };
  right: { label: string; value: string };
}) {
  const whole = sum(money(left.value), money(right.value));
  /* Sem nenhum dos dois valores, a barra fica vazia — e não toda de um lado. */
  const leftShare = whole > 0 ? money(left.value) / whole : 0;
  const rightShare = whole > 0 ? 1 - leftShare : 0;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-texto-suave">
        {label} · {whole > 0 ? format(whole) : 'sem valor informado'}
      </span>
      <div
        className="flex h-3 overflow-hidden rounded-pilula bg-superficie-suave"
        role="img"
        aria-label={`${left.label} ${brl(left.value)}, ${right.label.toLowerCase()} ${brl(right.value)}.`}
      >
        <span className="h-full bg-primaria" style={{ width: `${leftShare * 100}%` }} />
        <span
          className="h-full bg-primaria opacity-35"
          style={{ width: `${rightShare * 100}%` }}
        />
      </div>
      <div className="flex justify-between text-xs tabular-nums text-texto-suave">
        <span>
          {left.label} <b className="font-semibold text-texto">{brl(left.value)}</b>
        </span>
        <span>
          {right.label} <b className="font-semibold text-texto">{brl(right.value)}</b>
        </span>
      </div>
    </div>
  );
}
